/**
 * Slug minting and duplicate-name detection for attributes. Both live here so a
 * slug is built and compared by the same rules — a second home for either drifts.
 *
 * Detection runs in two scopes that are deliberately asymmetric:
 *
 * - A LOCAL only has to be unique *by display name* within its own space, against
 *   that space's other locals and the global catalog ({@link findAttributeNameConflict},
 *   DV-464/474). Two spaces may each define a local of the same display name; create
 *   paths append a random suffix so the machine slug stays catalog-unique (DV-517).
 * - A GLOBAL has to be unique against every local in every space
 *   ({@link findGlobalAttributeNameConflict}), because it can be adopted into any
 *   space and would then sit alongside that space's identically named local.
 *
 * Names compare case- and whitespace-insensitively, since "Source System" and
 * "source  system" read as one attribute. Slugs compare too: a slug is the backend
 * attribute name and is minted from the display name, so two differently spelled
 * names ("Deal Name", "Deal-Name") can still land on one identity.
 */
import type { GlobalAttribute, Space, SpaceLocalAttribute } from "#common/types/vault";

export type AttributeScope = "global" | "local";

export type AttributeNameConflict = {
  scope: AttributeScope;
  existingName: string;
  /** `name` — the display names read the same; `slug` — the machine slugs collide. */
  on: "name" | "slug";
  /**
   * Space owning the clashing local, when it need not be the one being edited — set
   * for a global candidate, which is checked against every space.
   */
  spaceName?: string;
};

/** A space-local attribute together with the space that owns it. */
export type ScopedLocalAttribute = {
  attribute: SpaceLocalAttribute;
  spaceId: string;
  spaceName: string;
};

/** Every space-local attribute in the vault, tagged with its owning space. */
export const localAttributesAcrossSpaces = (spaces: readonly Space[]): ScopedLocalAttribute[] =>
  spaces.flatMap((space) =>
    space.locals.map((attribute) => ({
      attribute,
      spaceId: space.id,
      spaceName: space.name,
    })),
  );

/** Pre-slug locals are keyed by their id. */
const localSlugOf = (local: SpaceLocalAttribute): string => local.slug ?? local.id;

export const normalizeAttributeName = (name: string): string =>
  name.trim().replace(/\s+/g, " ").toLowerCase();

/** The backend attribute `name`, minted from the display name and fixed thereafter. */
export const slugifyAttributeName = (name: string): string =>
  name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");

/**
 * Same alphabet/length as DVB `_mint_slug_suffix` — never derive from
 * ``Number.toString(36)``, which can yield fewer than four characters
 * (e.g. ``0.5`` → ``"0.i"`` → ``"i"``).
 */
const SLUG_SUFFIX_ALPHABET = "0123456789abcdefghijklmnopqrstuvwxyz";
const SLUG_SUFFIX_LEN = 4;

/**
 * Random tail for a *new* attribute's slug — four base-36 characters.
 *
 * A slug is the backend attribute `name`, unique workspace-wide across globals
 * AND every space's locals, so two attributes whose display names slugify the
 * same ("Region" after an earlier "region"; "Source System" in two spaces) are
 * rejected with `ATTRIBUTE_SLUG_CONFLICT` without it.
 *
 * Mint once per modal (`useState(newAttributeSlugSuffix)`), never per render:
 * the slug shown while the name is being typed has to be the exact one
 * submitted, and it is immutable after creation.
 */
export const newAttributeSlugSuffix = (): string => {
  let out = "";
  for (let i = 0; i < SLUG_SUFFIX_LEN; i += 1) {
    out += SLUG_SUFFIX_ALPHABET[Math.floor(Math.random() * SLUG_SUFFIX_ALPHABET.length)];
  }
  return out;
};

/** Slug stem for a name the slugifier reduces to nothing. */
const SLUG_FALLBACK_BASE = "attr";

/**
 * The slug for a new attribute: slugified display name plus a uniqueness tail.
 *
 * `slugifyAttributeName` keeps `[a-z0-9]` only, so a non-empty name can slugify
 * to NOTHING — "!!!", or any non-latin script. Those take {@link
 * SLUG_FALLBACK_BASE} as their stem rather than yielding a bare suffix, because
 * an empty stem broke two things at once: the field showed blank while create
 * submitted something else, and the duplicate-slug check skipped (it takes the
 * slug, and "" is falsy). A genuinely empty name mints nothing — there is
 * nothing to mint from yet, and Save is disabled anyway.
 */
export const mintAttributeSlug = (name: string, suffix: string): string => {
  if (name.trim() === "") return "";
  return `${slugifyAttributeName(name) || SLUG_FALLBACK_BASE}_${suffix}`;
};

/**
 * The first attribute a candidate collides with, or null. Globals are reported
 * ahead of locals: a global's name is the one the configurator cannot rename their
 * way out of.
 *
 * `globalAttributes` should be the whole catalog, not just the globals this space
 * references — an unreferenced global can be added later, and letting a local
 * pre-empt its name only defers the collision.
 */
export function findAttributeNameConflict({
  name,
  slug,
  globalAttributes,
  localAttributes,
  ignoreLocalId,
}: {
  name: string;
  /** Omit when editing an existing local: its slug is frozen and cannot collide. */
  slug?: string;
  globalAttributes: readonly GlobalAttribute[];
  localAttributes: readonly SpaceLocalAttribute[];
  ignoreLocalId?: string;
}): AttributeNameConflict | null {
  const candidate = normalizeAttributeName(name);
  // The required-field rule already covers an empty name; reporting a clash too
  // would put an error under a field the user has not filled in yet.
  if (!candidate) return null;

  const others = localAttributes.filter((l) => l.id !== ignoreLocalId);

  const globalByName = globalAttributes.find(
    (a) => normalizeAttributeName(a.name) === candidate,
  );
  if (globalByName) return { scope: "global", existingName: globalByName.name, on: "name" };

  const localByName = others.find((l) => normalizeAttributeName(l.name) === candidate);
  if (localByName) return { scope: "local", existingName: localByName.name, on: "name" };

  if (!slug) return null;

  // Globals key off the same backend attribute namespace a local slug lands in, so
  // `key` is the global's slug.
  const globalBySlug = globalAttributes.find((a) => a.key === slug);
  if (globalBySlug) return { scope: "global", existingName: globalBySlug.name, on: "slug" };

  const localBySlug = others.find((l) => localSlugOf(l) === slug);
  if (localBySlug) return { scope: "local", existingName: localBySlug.name, on: "slug" };

  return null;
}

const localConflict = (
  local: ScopedLocalAttribute,
  on: "name" | "slug",
): AttributeNameConflict => ({
  scope: "local",
  existingName: local.attribute.name,
  on,
  spaceName: local.spaceName,
});

/**
 * The same check for a GLOBAL candidate, whose scope is wider: a global is
 * available to every space, so it may not take the name of a local in ANY of them
 * — otherwise adopting the global into that space produces two fields reading the
 * same. Contrast {@link findAttributeNameConflict}, where a local only has to be
 * unique within its own space.
 */
export function findGlobalAttributeNameConflict({
  name,
  slug,
  globalAttributes,
  localAttributes,
  ignoreGlobalKey,
  ignoreLocal,
}: {
  name: string;
  /** Omit when editing an existing global: its key is frozen and cannot collide. */
  slug?: string;
  globalAttributes: readonly GlobalAttribute[];
  localAttributes: readonly ScopedLocalAttribute[];
  ignoreGlobalKey?: string;
  /**
   * The local being promoted to global — it does not clash with itself. Identified
   * by space as well, because local ids repeat across spaces.
   */
  ignoreLocal?: { spaceId: string; localId: string };
}): AttributeNameConflict | null {
  const candidate = normalizeAttributeName(name);
  if (!candidate) return null;

  const globals = globalAttributes.filter((a) => a.key !== ignoreGlobalKey);
  const locals = localAttributes.filter(
    (l) => l.spaceId !== ignoreLocal?.spaceId || l.attribute.id !== ignoreLocal?.localId,
  );

  const globalByName = globals.find((a) => normalizeAttributeName(a.name) === candidate);
  if (globalByName) return { scope: "global", existingName: globalByName.name, on: "name" };

  const localByName = locals.find(
    (l) => normalizeAttributeName(l.attribute.name) === candidate,
  );
  if (localByName) return localConflict(localByName, "name");

  if (!slug) return null;

  const globalBySlug = globals.find((a) => a.key === slug);
  if (globalBySlug) return { scope: "global", existingName: globalBySlug.name, on: "slug" };

  const localBySlug = locals.find((l) => localSlugOf(l.attribute) === slug);
  if (localBySlug) return localConflict(localBySlug, "slug");

  return null;
}

/** Where the clash lives, e.g. `a space-local attribute in “Trial Balance”`. */
export function attributeConflictLocation(conflict: AttributeNameConflict): string {
  if (conflict.scope === "global") return "a global attribute";
  return conflict.spaceName
    ? `a space-local attribute in “${conflict.spaceName}”`
    : "a space-local attribute in this space";
}

/**
 * The instruction every name-clash message ends on, wherever the clash was
 * detected. Shared with the server-refusal wording in
 * `DocumentTypeSettings/errors.ts` so the two cannot drift apart — a steward
 * reads them in the same field, one after the other.
 */
export const CHOOSE_A_DIFFERENT_NAME = "Choose a different name.";

/** Single source of the wording, so every surface reports a clash identically. */
export function attributeNameConflictMessage(conflict: AttributeNameConflict): string {
  const where = attributeConflictLocation(conflict);
  return conflict.on === "name"
    ? `“${conflict.existingName}” is already ${where}. ${CHOOSE_A_DIFFERENT_NAME}`
    : `This name mints the same slug as ${where}, “${conflict.existingName}”. ${CHOOSE_A_DIFFERENT_NAME}`;
}
